import MaterialTable from "@material-table/core";
import { Tooltip, Typography, useTheme } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Refresh } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import LinkButton from "../components/LinkButton/LinkButton";
import { usePopups } from "../contexts/PopupContext";
import { useGlobalSnackbar } from "../contexts/SnackbarContext";
import { useUser } from "../contexts/UserContextProvider";
import useApi from "../hooks/useApi";
import { DEMO_PATIENT_DEV, DEMO_PATIENT_PROD } from "../utils/utils";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  content: {
    fontSize: "3ex",
  },
  card: {
    padding: "2%",
  },
  title: {
    fontWeight: "lighter",
    paddingBottom: "1%",
  },
});

export default function Therapists() {
  const classes = useStyles();
  const { t } = useTranslation(["views", "common"]);
  const history = useHistory();

  const [items, setItems] = useState([]);

  const [user] = useUser();
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const api = useApi();
  const [, open] = usePopups();

  const setGlobalSnackbar = useGlobalSnackbar();

  const loadPatients = async () => {
    setLoading(true);
    const response = await api(
      `patients/${user?.profile?.clinicCode}/cliniccode`,
      "GET",
      null,
      () => {
        open("sessionExpiredPopup");
      },
    );
    setLoading(false);
    if (response.ok === false) {
      setGlobalSnackbar({
        open: true,
        severity: "error",
        message: `${t("common:errors.patients")}`,
      });
      return;
    }
    const filteredPatients = response.filter(
      (patient) =>
        ![DEMO_PATIENT_DEV, DEMO_PATIENT_PROD].includes(patient.idPatient),
    );
    setItems(filteredPatients);
  };

  useEffect(() => {
    loadPatients();
  }, []);

  return (
    <div className={classes.card}>
      <Typography className={classes.title} variant="h4">
        {t("views:patients.title")}
      </Typography>
      <div className={classes.content}>
        <MaterialTable
          isLoading={loading}
          theme={theme}
          columns={[
            {
              title: `${t("common:label.code")}`,
              field: "patientCode",
              type: "string",
              align: "left",
              customSort: (a, b) => {
                const codeA = (a.patientCode ?? "").toString().toLowerCase();
                const codeB = (b.patientCode ?? "").toString().toLowerCase();

                if (codeA < codeB) return -1;
                if (codeA > codeB) return 1;
                return 0;
              },
              render: (rowData) => rowData.patientCode ?? "-",
            },
            {
              title: `${t("common:label.patient")}`,
              defaultSort: "asc",
              field: "patientFullName",
              customSort: (a, b) => {
                const nameA = `${a.lastName} ${a.name}`.toLowerCase();
                const nameB = `${b.lastName} ${b.name}`.toLowerCase();
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
              },
              customFilterAndSearch: (term, rowData) =>
                `${rowData.lastName} ${rowData.name}`
                  .toLowerCase()
                  .includes(term.toLowerCase()),
              render: (rowData) => (
                <Tooltip title={t("common:tooltip.patient_profile")}>
                  <span>
                    <LinkButton
                      onClick={() => {
                        history.push(`patients/${rowData.idPatient}`);
                      }}
                    >{`${rowData.lastName ?? ""} ${
                      rowData.name ?? ""
                    }`}</LinkButton>
                  </span>
                </Tooltip>
              ),
              filterPlaceholder: "Cerca per Cognome",
            },
            {
              title: `${t("common:label.therapist")}`,
              field: "therapistFullName",
              customSort: (a, b) => {
                const nameA =
                  `${a.therapistLastName} ${a.therapistFirstName}`.toLowerCase();
                const nameB =
                  `${b.therapistLastName} ${b.therapistFirstName}`.toLowerCase();
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
              },
              customFilterAndSearch: (term, rowData) =>
                `${rowData.therapistLastName} ${rowData.therapistFirstName}`
                  .toLowerCase()
                  .includes(term.toLowerCase()),
              render: (rowData) =>
                `${rowData.therapistLastName ?? ""} ${
                  rowData.therapistFirstName ?? ""
                }`,
            },
            {
              title: `${t("common:label.state")}`,
              type: "string",
              render: (rowData) =>
                rowData.isArchived
                  ? `${t("common:label.archived")}`
                  : `${t("common:label.active")}`,
              customSort: (a, b) => {
                const valA = a.isArchived ? 1 : 0;
                const valB = b.isArchived ? 1 : 0;
                return valA - valB;
              },
            },
          ]}
          actions={[
            {
              icon: Refresh,
              isFreeAction: true,
              onClick: loadPatients,
            },
          ]}
          data={items}
          options={{
            draggable: false,
            showTitle: false,
            thirdSortClick: false,
            toolbar: true,
            rowStyle: (rowData) => ({
              fontSize: "1rem",
            }),
          }}
          localization={{
            header: {
              actions: "",
            },
            body: {
              emptyDataSourceMessage: (
                <>
                  {t("views:patients.empty_table_title")} <br />
                  {t("views:patients.empty_table_subtitle")}
                </>
              ),
            },
            toolbar: {
              searchPlaceholder: `${t("common:table.search_placeholder")}`,
              searchTooltip: `${t("common:table.search_tooltip")}`,
            },
            pagination: {
              labelDisplayedRows: `{from}-{to} ${t("common:table.displayed_rows")} {count}`,
              labelRowsSelect: `${t("common:table.rows")}`,
              labelRowsPerPage: `${t("common:table.rows_page")}`,
              firstTooltip: `${t("common:table.first_tooltip")}`,
              lastTooltip: `${t("common:table.last_tooltip")}`,
              nextTooltip: `${t("common:table.next_tooltip")}`,
              previousTooltip: `${t("common:table.previous_tooltip")}`,
            },
          }}
        />
      </div>
    </div>
  );
}
